import React, { useEffect, useState } from "react";
import {
  Box,
  Button as MuiButton,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import PageHeader1 from "../../components/common/PageHeader1";
import { useDispatch, useSelector } from "react-redux";
import { fetchRechargeReport, exportRechargeReport } from "../../Redux/Reducers/CallAndChatReducer";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermissions } from "../../context/AuthContext";

const InvoiceReportList = () => {
  const { can } = usePermissions();
  const dispatch = useDispatch();
  const {
    data: rowsData = [],
    paginationDetail = {},
    status = "idle",
  } = useSelector((state) => state?.CallAndChatReducer || {});

  const [paginationModel, setPaginationModel] = useState(() => ({
    page: paginationDetail?.currentPage ? paginationDetail.currentPage - 1 : 0,
    pageSize: paginationDetail?.perPage || 10,
  }));

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);

  // Search and date filter state
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }

    const page = paginationModel.page + 1;
    const limit = paginationModel.pageSize;
    const params = {
      page,
      limit,
      paymentStatus: "success",
    };

    // Add date filters if applied
    if (appliedStartDate) {
      params.startDate = appliedStartDate;
    }
    if (appliedEndDate) {
      params.endDate = appliedEndDate;
    }

    dispatch(fetchRechargeReport(params));
  }, [dispatch, paginationModel, appliedStartDate, appliedEndDate, appliedSearch]);

  // Calculate rowCount: use paginationDetail if available, otherwise use data length (for search)
  const rowCount = appliedSearch
    ? (rowsData?.length || 0)
    : (paginationDetail?.totalRecords || 0);

  const rows = rowsData.map((item, index) => ({
    ...item,
    id: item._id || item.id || index,
    sono: paginationModel.page * paginationModel.pageSize + index + 1,
    createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString()
      : "",
  }));

  const handleViewPdf = async (pdfUrl, transactionId) => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } else {
      // Generate the invoice report
      const token = localStorage.getItem("vedhvidhi-admin-token");
      if (!token) {
        toast.error("Authentication token not found. Please log in again.");
        return;
      }

      const loadingToastId = toast.loading("Generating invoice report...");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/generateInvoice/${transactionId}`,
          {
            method: "POST",
            headers: {
              Authorization: `${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || "Failed to generate invoice report.");
        }

        // Open the generated PDF URL
        if (result.data?.invoicePdfUrl) {
          toast.dismiss(loadingToastId);
          window.open(result.data.invoicePdfUrl, "_blank", "noopener,noreferrer");
          toast.success("Invoice report generated successfully!");
          // Reload the page after a short delay
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          toast.dismiss(loadingToastId);
          toast.error("Invoice report URL not found in response.");
        }
      } catch (error) {
        console.error("Error generating invoice report:", error);
        toast.dismiss(loadingToastId);
        toast.error(`Error: ${error.message}`);
      }
    }
  };

  const handleCreditNoteClick = (invoiceNumber) => {
    setInvoiceToCancel(invoiceNumber);
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
    setInvoiceToCancel(null);
  };

  const handleConfirmCreditNote = async () => {
    if (!invoiceToCancel) return;

    const token = localStorage.getItem("vedhvidhi-admin-token");
    const apiUrl = `${process.env.REACT_APP_BASEURL}/admin/creditNote`;

    if (!token) {
      toast.error("Authentication token not found. Please log in again.");
      handleCloseConfirm();
      return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          cancelledInvoice: invoiceToCancel,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to create credit note.");
      }

      toast.success("Credit note created successfully!");
    } catch (error) {
      console.error("Error creating credit note:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      handleCloseConfirm();
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const resultAction = await dispatch(
        exportRechargeReport({
          paymentStatus: "success",
          search: appliedSearch,
          startDate: appliedStartDate,
          endDate: appliedEndDate,
        })
      );
      const payload = resultAction.payload;

      if (!payload?.data || payload.data.length === 0) {
        toast.warn("No data to export");
        return;
      }

      const exportData = payload.data.map((item, index) => ({
        "S.No.": index + 1,
        "User Name": item.fullName,
        "Mobile Number": item.mobileNumber,
        "Invoice Number": item.invoiceNumber,
        Segment: item.segment,
        "Basic Amount": item.rechargeAmount,
        State: item.state,
        SGST: item.sgst || "N/A",
        CGST: item.cgst || "N/A",
        IGST: item.igst || "N/A",
        "Invoice Amount": item.invoiceAmount,
        "Created At": item.createdAt ? new Date(item.createdAt).toLocaleString() : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "InvoiceReport");
      XLSX.writeFile(
        workbook,
        `Invoice_Report_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success("Export successful!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  const handleSearch = () => {
    if (!search.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchRechargeReport({ page: 1, limit: 10, paymentStatus: "success" }));
      return;
    }
    setAppliedSearch(search);
    // Call API with only search parameter (no page/limit)
    dispatch(fetchRechargeReport({ search: search, paymentStatus: "success" }));
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchRechargeReport({ page: 1, limit: 10, paymentStatus: "success" }));
    }
  };

  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start date and end date");
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSearch(""); // Clear search when applying date filter
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
    const page = 1;
    const limit = 10;
    dispatch(fetchRechargeReport({
      page,
      limit,
      paymentStatus: "success",
      startDate: startDate,
      endDate: endDate,
    }));
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setAppliedSearch("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setPaginationModel({ page: 0, pageSize: 10 });
    dispatch(fetchRechargeReport({ page: 1, limit: 10, paymentStatus: "success" }));
  };

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "fullName", headerName: "User Name", width: 150 },
    { field: "mobileNumber", headerName: "Mobile No.", width: 140 },
    { field: "invoiceNumber", headerName: "Invoice No.", width: 220 },
    { field: "segment", headerName: "Segment", width: 120 },
    { field: "rechargeAmount", headerName: "Basic Amount", width: 130 },
    { field: "state", headerName: "State", width: 120 },
    { field: "sgst", headerName: "SGST", width: 100 },
    { field: "cgst", headerName: "CGST", width: 100 },
    { field: "igst", headerName: "IGST", width: 100 },
    { field: "invoiceAmount", headerName: "Invoice Amount", width: 150 },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="View/Open PDF">
            <IconButton onClick={() => handleViewPdf(row.invoicePdfUrl, row?.transactionId || row?._id || row?.id)}>
              <i className="icofont-eye text-info"></i>
            </IconButton>
          </Tooltip>
          {can('credit_note_report:view') && (
            <Tooltip title="Create Credit Note">
              <IconButton onClick={() => handleCreditNoteClick(row.invoiceNumber)}>
                <i className="icofont-file-document text-danger"></i>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const exportToExcel = () => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const exportData = rows.map((row) => ({
      "S.No.": row.sono,
      "User Name": row.fullName,
      "Mobile Number": row.mobileNumber,
      "Invoice Number": row.invoiceNumber,
      Segment: row.segment,
      "Basic Amount": row.rechargeAmount,
      State: row.state,
      SGST: row.sgst || "N/A",
      CGST: row.cgst || "N/A",
      IGST: row.igst || "N/A",
      "Invoice Amount": row.invoiceAmount,
      "Created At": new Date(row.createdAt).toLocaleString(),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "InvoiceReport");
    XLSX.writeFile(workbook, `Invoice_Report_${new Date().toISOString()}.xlsx`);
  };

  return (
    <div className="body d-flex py-1">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="container-xxl">
        <PageHeader1 pagetitle="Invoice Report" />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search and Date Filter Section - Inside table card, single line */}
                  <Box display="flex" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    <TextField
                      placeholder="Search..."
                      value={search}
                      onChange={handleSearchChange}
                      size="small"
                      sx={{ width: '250px' }}
                    />
                    <IconButton
                      onClick={handleSearch}
                      disabled={status === "loading" || !search.trim()}
                      sx={{
                        height: '40px',
                        width: '40px',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '&:disabled': {
                          backgroundColor: 'action.disabledBackground',
                          color: 'action.disabled',
                        }
                      }}
                    >
                      <FaSearch />
                    </IconButton>
                    <TextField
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <TextField
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleApplyFilter}
                      disabled={status === "loading"}
                      sx={{ height: '40px' }}
                    >
                      {status === "loading" ? "Loading..." : "Apply"}
                    </Button>
                    <MuiButton variant="contained" color="success" onClick={handleExport} sx={{ height: '40px' }}>
                      Export
                    </MuiButton>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleClearFilters}
                      sx={{ height: '40px' }}
                    >
                      Clear
                    </Button>
                  </Box>

                  <Box sx={{ height: 600 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      getRowId={(row) => row.id}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode={appliedSearch ? "client" : "server"}
                      loading={status === "loading"}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isConfirmOpen} onClose={handleCloseConfirm}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to create a credit note for invoice{" "}
            <strong>{invoiceToCancel}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCloseConfirm}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleConfirmCreditNote} color="primary" autoFocus>
            Confirm
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InvoiceReportList;